import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2, Check, ChevronRight, CircleCheck, Globe2, History, Loader2,
  MapPin, Package, Route, Send, Truck, UploadCloud, X, XCircle,
} from 'lucide-react';
import {
  ComposableMap, Geographies, Geography, Line, Marker, ZoomableGroup,
} from 'react-simple-maps';

import { useXomicsAuth } from './AuthContext';
import {
  dispatchCarrierShipment, loadCarrierQuotes, loadLocations, loadSample,
  loadSampleActivity, loadSampleStages, loadSamples, purchaseCarrierLabel,
  searchSamples,
  recordSampleStep,
} from './api';
import {
  Empty, Failed, Head, Loading, RailHeader, SearchField, WorkspaceTabs, XomicsSelect,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel } from './labels';
import {
  coordinateFor, curvedRoute, GEO_URL, mapView, sameCoordinates,
} from './routeMap';
import ShippingLabelButton from './ShippingLabelButton';
import SampleLabProgress from './SampleLabProgress';
import SampleOrganizationCodes from './SampleOrganizationCodes';

const MAP_MARKER_SPACING = 18;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const KIND_TONE = {
  activation: 'info', custody: 'ok', transit: 'warn', processing: 'info', output: 'ok',
};

function toneOf(kind) {
  return KIND_TONE[kind] ?? 'flat';
}

function when(value) {
  if (value === null || value === undefined) return '—';
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function money(minor, currency) {
  return `${(Number(minor) / 100).toFixed(2)} ${currency}`;
}

function normalizeDestinationKind(kind) {
  return kind === 'lab' ? 'laboratory' : kind;
}

function requiredLocationKind(stage) {
  if (stage === null || stage === undefined) return null;
  if (stage.kind === 'transit') return normalizeDestinationKind(stage.destinationKind);
  if (stage.actor === 'lab') return 'laboratory';
  return null;
}

function activationLocation(sample) {
  const origin = sample?.activationOrigin;
  if (origin === null || origin === undefined) return null;
  const latitude = origin.latitude === null || origin.latitude === undefined
    ? null : Number(origin.latitude);
  const longitude = origin.longitude === null || origin.longitude === undefined
    ? null : Number(origin.longitude);
  const coordinates = Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
      && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180
    ? [longitude, latitude] : null;
  const demo = typeof origin.cloudflareRay === 'string' && origin.cloudflareRay.startsWith('DEMO-ORIGIN-');
  const city = demo && origin.city ? origin.city.replace(/^Demo · /, '') : origin.city;
  const country = origin.countryCode
    ? new Intl.DisplayNames([document.documentElement.lang], { type: 'region' }).of(origin.countryCode)
    : null;
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const places = [city, origin.region, country].filter(Boolean);
  const label = places.filter((value, index) => places.findIndex(place => normalize(place) === normalize(value)) === index).join(', ');
  return {
    key: `activation:${sample.uuid}`,
    code: origin.city ?? origin.postalCode ?? origin.countryCode ?? tx('ui.samples.activation'),
    label: label || tx('ui.samples.activation_origin_301cd519'),
    kind: 'activation',
    demo,
    countryCode: origin.countryCode,
    coordinates,
  };
}

function locationLabel(location) {
  return location?.label ?? location?.code ?? tx('ui.samples.no_physical_checkpoint');
}

function mapLocationText(location) {
  if (location.kind === 'activation') {
    return { title: location.label, detail: location.demo ? tx('ui.samples.demo_activation') : null };
  }
  return {
    title: location.name ?? location.code,
    detail: [location.code, location.address?.city].filter(Boolean).join(' · '),
  };
}

function mapCheckpointText(point) {
  const location = mapLocationText(point.location);
  return {
    title: location.title,
    detail: [codeLabel('sampleStage', point.event.stage.code), location.detail].filter(Boolean).join(' · '),
  };
}

function mapLocationAddress(location) {
  // Activation has a place inferred from request metadata, not a street address.
  if (location.kind === 'activation') return null;
  const address = location.address;
  if (address === null || address === undefined) return null;
  const locality = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
  return [address.line1, address.line2, locality, address.countryCode].filter(Boolean).join(' · ');
}

function isTestActivation(event) {
  return event?.stage.code === 'activated'
    && /^TEST collection activation batch [0-9a-f-]{36}$/i.test(event.note || '');
}

function ActivationHistoryDetails({ sample, event, location }) {
  const origin = sample.activationOrigin;
  const coordinates = location?.coordinates;
  const fields = [
    [tx('ui.samples.activation_email'), event.by?.email],
    [tx('ui.samples.activation_ip'), origin?.ip],
    [tx('ui.samples.activation_timezone'), origin?.timezone],
    [tx('ui.samples.activation_coordinates'), coordinates ? `${coordinates[1]}, ${coordinates[0]}` : null],
    [tx('ui.samples.activation_request'), origin?.cloudflareRay],
    [tx('ui.samples.activation_reference'), isTestActivation(event) ? event.note : null],
  ].filter(([, value]) => value);
  return (
    <div className="xo-sample-activation-copy">
      <strong><MapPin aria-hidden="true" />{location === null
        ? tx('ui.samples.activation_location_missing') : location.label}</strong>
      {event.by && <span>{tx('ui.samples.activated_by', { name: event.by.name || event.by.email })}</span>}
      {event.note && !isTestActivation(event) && <p>{event.note}</p>}
      {fields.length > 0 && (
        <details className="xo-sample-activation-technical">
          <summary>{tx('ui.samples.technical_details')}</summary>
          <dl>{fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
        </details>
      )}
    </div>
  );
}

function actorLabel(actor) {
  const keys = {
    customer: 'ui.samples.actor_customer',
    office: 'ui.samples.actor_office',
    lab: 'ui.samples.actor_laboratory',
    system: 'ui.samples.actor_system',
  };
  return tx(keys[actor] || 'ui.common.unknown');
}

function locationForEvent(event, locationsById, sample) {
  if (event.stage?.code === 'activated') {
    const activation = activationLocation(sample);
    if (activation !== null) return activation;
  }
  const reference = event.location ?? event.shipment?.destination ?? null;
  if (reference === null) return null;
  return locationsById.get(Number(reference.id)) ?? reference;
}

function rawMapCoordinate([longitude, latitude]) {
  const lambda = longitude * Math.PI / 180;
  const phi = Math.max(-85, Math.min(85, latitude)) * Math.PI / 180;
  return [450 + 145 * lambda, 260 - 145 * Math.log(Math.tan((Math.PI / 2 + phi) / 2))];
}

function unprojectMapCoordinate([x, y]) {
  const longitude = ((x - 450) / 145) * 180 / Math.PI;
  const mercator = (260 - y) / 145;
  const latitude = (2 * Math.atan(Math.exp(mercator)) - Math.PI / 2) * 180 / Math.PI;
  return [longitude, latitude];
}

function projectMapCoordinate(coordinates, view) {
  const point = rawMapCoordinate(coordinates);
  const center = rawMapCoordinate(Array.isArray(view.center) ? view.center : [0, 0]);
  return [
    450 + (point[0] - center[0]) * view.zoom,
    260 + (point[1] - center[1]) * view.zoom,
  ];
}

function viewFromTransform({ x, y, zoom }, fallbackCenter) {
  if (![x, y, zoom].every(Number.isFinite) || zoom <= 0) {
    return { center: fallbackCenter, zoom: Number.isFinite(zoom) ? zoom : 1 };
  }
  return {
    center: unprojectMapCoordinate([(450 - x) / zoom, (260 - y) / zoom]),
    zoom,
  };
}

function floatingPanelPosition(viewport, x, y) {
  const width = Math.min(250, Math.max(180, viewport.width - 16));
  let left = x - width / 2;
  left = Math.max(8, Math.min(left, viewport.width - width - 8));
  const top = y + 14;
  if (top > viewport.height - 56) return null;
  return { left, top, maxHeight: viewport.height - top - 8 };
}

function focusedMapView(coordinates, zoom) {
  const point = rawMapCoordinate(coordinates);
  const target = [450, 165];
  return {
    center: unprojectMapCoordinate([
      point[0] - (target[0] - 450) / zoom,
      point[1] - (target[1] - 260) / zoom,
    ]),
    zoom,
  };
}

function overlapArea(left, right) {
  const width = Math.max(0, Math.min(left.right, right.right) - Math.max(left.left, right.left));
  const height = Math.max(0, Math.min(left.bottom, right.bottom) - Math.max(left.top, right.top));
  return width * height;
}

function placeMapLabels(items, view) {
  const placements = new Map();
  const occupied = items.filter((item) => !item.centered).map((item) => {
    const projected = projectMapCoordinate(item.coordinates, view);
    const x = projected[0] + (item.offsetX ?? 0);
    const y = projected[1] + (item.offsetY ?? 0);
    const width = item.obstacleWidth ?? 16;
    return { left: x - width / 2, right: x + width / 2, top: y - 8, bottom: y + 8 };
  });

  items.forEach((item) => {
    const projected = projectMapCoordinate(item.coordinates, view);
    const x = projected[0] + (item.offsetX ?? 0);
    const y = projected[1] + (item.offsetY ?? 0);
    const clearance = item.clearance ?? 10;
    const horizontal = clearance + item.width / 2;
    const vertical = clearance + item.height / 2;
    const defaultCandidates = [
      [0, vertical], [0, -vertical], [horizontal, 0], [-horizontal, 0],
      [horizontal * 0.78, vertical * 0.78], [-horizontal * 0.78, vertical * 0.78],
      [horizontal * 0.78, -vertical * 0.78], [-horizontal * 0.78, -vertical * 0.78],
    ];
    const candidates = item.centered
      ? [[0, 0], [0, -vertical], [0, vertical], [horizontal, 0], [-horizontal, 0]]
      : item.sideFirst
        ? [defaultCandidates[2], defaultCandidates[3], ...defaultCandidates.slice(0, 2), ...defaultCandidates.slice(4)]
        : defaultCandidates;
    const options = candidates.map(([dx, dy]) => {
      const box = {
        left: x + dx - item.width / 2,
        right: x + dx + item.width / 2,
        top: y + dy - item.height / 2,
        bottom: y + dy + item.height / 2,
      };
      const outside = Math.max(0, 8 - box.left) + Math.max(0, box.right - 892)
        + Math.max(0, 8 - box.top) + Math.max(0, box.bottom - 512);
      const collision = occupied.reduce((total, other) => total + overlapArea(box, other), 0);
      return { dx, dy, box, score: collision + outside * 1000 };
    });
    const selected = options.sort((left, right) => left.score - right.score)[0];
    const side = Math.abs(selected.dx) < 0.001 ? 'center' : selected.dx > 0 ? 'right' : 'left';
    placements.set(item.key, {
      dx: selected.dx,
      dy: selected.dy,
      textAnchor: item.centered || side === 'center' ? 'middle' : side === 'right' ? 'start' : 'end',
      textX: item.centered || side === 'center' ? 0 : side === 'right' ? -item.width / 2 : item.width / 2,
    });
    occupied.push(selected.box);
  });
  return placements;
}

function readableCode(value) {
  if (!value) return null;
  const keys = {
    activation: 'ui.samples.kind_activation',
    custody: 'ui.samples.kind_custody',
    transit: 'ui.samples.kind_transit',
    processing: 'ui.samples.kind_processing',
    output: 'ui.samples.kind_output',
    created: 'ui.samples.shipment_created_72975854',
    label_printed: 'ui.samples.shipment_label_printed',
    dispatched: 'ui.samples.shipment_dispatched',
    in_transit: 'ui.samples.shipment_in_transit',
    delivered: 'ui.samples.shipment_delivered',
    returned: 'ui.samples.shipment_returned',
    lost: 'ui.samples.shipment_lost',
    cancelled: 'ui.values.shipment_cancelled',
  };
  return tx(keys[String(value).trim().toLowerCase()] || 'ui.common.unknown');
}

function JourneyDetailPanel({ detail, position, onClose }) {
  if (detail === null || position === null) return null;

  const { event, location, origin, pending } = detail;
  const transit = event.stage.kind === 'transit';
  const activation = event.stage.code === 'activated';
  const destinationCopy = location === null ? null : mapLocationText(location);
  const originCopy = origin === null ? null : mapLocationText(origin);
  const status = pending
    ? tx('ui.samples.journey_in_progress')
    : activation ? tx('ui.samples.activation_origin_301cd519')
      : transit ? tx('ui.samples.transit_recorded') : tx('ui.samples.exact_checkpoint');
  return (
    <aside
      className="xo-sample-map-panel is-floating"
      style={{ left: position.left, top: position.top, maxHeight: position.maxHeight }}
    >
      <header>
        <span className={pending ? 'is-pending' : transit ? 'is-transit' : 'is-exact'}>
          {transit ? <Truck aria-hidden="true" /> : <MapPin aria-hidden="true" />}
        </span>
        <div><small>{status}</small><h3>{codeLabel('sampleStage', event.stage.code)}</h3></div>
        <button
          type="button"
          className="xo-sample-map-panel-close"
          aria-label={tx("ui.samples.close_map_detail_89d5600b")}
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>
      </header>

      {transit ? (
        <div className="xo-sample-map-route-detail">
          <div><small>{tx("ui.samples.from_3f66052a")}</small><strong>{originCopy?.title ?? tx("ui.samples.unmapped_origin_673ecabc")}</strong><span>{originCopy?.detail}</span></div>
          <ChevronRight aria-hidden="true" />
          <div><small>{tx("ui.samples.to_ae79ea1e")}</small><strong>{destinationCopy?.title ?? tx("ui.samples.unmapped_destination_a1153fc9")}</strong><span>{destinationCopy?.detail}</span></div>
        </div>
      ) : destinationCopy !== null && (
        <div className="xo-sample-map-place">
          <Building2 aria-hidden="true" />
          <div>
            <strong>{destinationCopy.title}</strong>
            <span>{destinationCopy.detail}</span>
            {mapLocationAddress(location) && <small>{mapLocationAddress(location)}</small>}
          </div>
        </div>
      )}

      <dl>
        <div><dt>{tx("ui.samples.recorded_d5383ea7")}</dt><dd>{when(event.occurredAt)}</dd></div>
        {!activation && <div><dt>{tx("ui.samples.responsible_96cd625f")}</dt><dd>{actorLabel(event.stage.actor)}</dd></div>}
      </dl>

      {transit && (
        <div className="xo-sample-map-shipment">
          <Truck aria-hidden="true" />
          {event.shipment === null || event.shipment === undefined ? (
            <div>
              <strong>{tx("ui.samples.self_arranged_movement_9c2dd801")}</strong>
              <span>{tx("ui.samples.no_managed_shipment_is_linked_to_this_event_15391d1e")}</span>
            </div>
          ) : (
            <div>
              <strong>{event.shipment.carrier?.name ?? event.shipment.carrier?.code ?? tx("ui.samples.managed_shipment_acdb55fd")}</strong>
              <span>{event.shipment.trackingNumber ?? tx("ui.samples.tracking_pending_5455cb5a")}</span>
              <small>{readableCode(event.shipment.status) ?? tx("ui.samples.shipment_created_72975854")}</small>
            </div>
          )}
        </div>
      )}
      {event.note && !isTestActivation(event) && <p className="xo-sample-map-note">{event.note}</p>}
      {event.by && <footer>{tx("ui.samples.recorded_by_value_5bd17e68", { value: event.by.name || event.by.email })}</footer>}
    </aside>
  );
}

function JourneyMap({ uuid, locations, revision }) {
  const [hoverCard, setHoverCard] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [mapPosition, setMapPosition] = useState(null);
  const [focusedView, setFocusedView] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 900, height: 520, scale: 1 });
  const viewportRef = useRef(null);
  const animationRef = useRef(null);
  const focusTimerRef = useRef(null);
  const load = useCallback(() => {
    // The revision is an explicit cache-buster after a journey mutation.
    void revision;
    return loadSample(uuid);
  }, [uuid, revision]);
  const journey = useLiveResource(['samples'], load);
  const changingSample = journey.data !== null
    && String(journey.data.sample.uuid) !== String(uuid);
  const mapBusy = journey.loading || (changingSample && journey.error === null);
  const locationsById = useMemo(
    () => new Map(locations.map((location) => [Number(location.id), location])),
    [locations],
  );

  const points = useMemo(() => {
    if (journey.data === null) return [];
    return journey.data.history.map((event, index) => {
      const location = locationForEvent(event, locationsById, journey.data.sample);
      const coordinates = coordinateFor(location);
      return coordinates === null ? null : {
        event, location, coordinates, sequence: index + 1,
      };
    }).filter(Boolean);
  }, [journey.data, locationsById]);

  const markers = useMemo(() => {
    const grouped = new Map();
    points.filter((point) => point.event.stage.kind !== 'transit').forEach((point) => {
      const key = point.coordinates.map((coordinate) => coordinate.toFixed(5)).join(':');
      const group = grouped.get(key);
      if (group) group.push(point);
      else grouped.set(key, [point]);
    });
    return [...grouped.values()].flatMap((group) => group.map((point, index) => ({
      ...point,
      markerOffset: (index - (group.length - 1) / 2) * MAP_MARKER_SPACING,
    })));
  }, [points]);
  const legs = useMemo(() => points.slice(1).map((point, index) => ({
    event: point.event,
    sequence: point.sequence,
    from: points[index].coordinates,
    to: point.coordinates,
  })).filter((leg) => !sameCoordinates(leg.from, leg.to)).map((leg) => {
    const route = curvedRoute(leg.from, leg.to);
    return { ...leg, route, midpoint: route[Math.floor(route.length / 2)] };
  }), [points]);
  const pendingDestination = useMemo(() => {
    if (journey.data === null || journey.data.history.length === 0) return null;
    const event = journey.data.history[journey.data.history.length - 1];
    if (event.stage.kind !== 'transit') return null;
    const location = locationForEvent(event, locationsById, journey.data.sample);
    const coordinates = coordinateFor(location);
    return location === null || coordinates === null
      ? null
      : {
        event, location, coordinates, copy: mapLocationText(location),
      };
  }, [journey.data, locationsById]);
  const view = useMemo(() => mapView([
    ...points,
    ...legs.flatMap((leg) => leg.route.map((coordinates) => ({ coordinates }))),
  ]), [points, legs]);

  const animateMapBetween = useCallback((from, next, duration = 520) => {
    if (animationRef.current !== null) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    const startedAt = window.performance.now();
    let longitudeDelta = next.center[0] - from.center[0];
    if (longitudeDelta > 180) longitudeDelta -= 360;
    if (longitudeDelta < -180) longitudeDelta += 360;
    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - (1 - progress) ** 3;
      const frame = {
        center: [
          ((from.center[0] + longitudeDelta * eased + 540) % 360) - 180,
          from.center[1] + (next.center[1] - from.center[1]) * eased,
        ],
        zoom: from.zoom + (next.zoom - from.zoom) * eased,
      };
      setMapPosition(frame);
      setFocusedView(frame);
      if (progress < 1) animationRef.current = window.requestAnimationFrame(tick);
      else animationRef.current = null;
    };
    animationRef.current = window.requestAnimationFrame(tick);
  }, []);

  useLayoutEffect(() => {
    if (animationRef.current !== null) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
    setMapPosition(view);
    setFocusedView(view);
    setHoverCard(null);
    setSelectedCard(null);
    const latest = points[points.length - 1] ?? null;
    if (latest === null) return undefined;

    const anchor = latest.event.stage.kind === 'transit' ? 'transit' : 'checkpoint';
    const latestLeg = anchor === 'transit'
      ? legs.find((leg) => Number(leg.event.id) === Number(latest.event.id))
      : null;
    const coordinates = latestLeg?.midpoint ?? latest.coordinates;
    const zoom = Math.min(32, Math.max(view.zoom + 0.35, view.zoom * 1.16));
    const focused = focusedMapView(coordinates, zoom);
    const focusCurrent = () => {
      setSelectedCard({ eventId: Number(latest.event.id), anchor });
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        setMapPosition(focused);
        setFocusedView(focused);
      } else {
        animateMapBetween(view, focused, 480);
      }
      focusTimerRef.current = null;
    };
    focusTimerRef.current = window.setTimeout(focusCurrent, 180);
    return () => {
      if (focusTimerRef.current !== null) {
        window.clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
  }, [animateMapBetween, legs, points, view]);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return undefined;
    const measure = () => {
      const bounds = viewport.getBoundingClientRect();
      const rawScale = Math.min(bounds.width / 900, bounds.height / 520);
      const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
      setViewportSize((current) => (
        Math.abs(current.width - bounds.width) < 0.5
        && Math.abs(current.height - bounds.height) < 0.5
        && Math.abs(current.scale - scale) < 0.001
          ? current
          : { width: bounds.width, height: bounds.height, scale }
      ));
    };
    measure();
    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(measure);
      observer.observe(viewport);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [journey.data]);
  useEffect(() => () => {
    if (animationRef.current !== null) window.cancelAnimationFrame(animationRef.current);
    if (focusTimerRef.current !== null) window.clearTimeout(focusTimerRef.current);
  }, []);
  const activeView = mapPosition ?? focusedView ?? view;
  const layoutScale = Math.min(1, Math.max(0.08, viewportSize.scale));
  const mapZoom = activeView.zoom;
  const visualScale = mapZoom * layoutScale;
  const visibleCard = selectedCard ?? hoverCard;
  const hoverDetail = useMemo(() => {
    if (journey.data === null || journey.data.history.length === 0 || visibleCard === null) return null;
    const history = journey.data.history;
    const index = history.findIndex((event) => Number(event.id) === Number(visibleCard.eventId));
    if (index < 0) return null;
    const event = history[index];
    const location = locationForEvent(event, locationsById, journey.data.sample);
    let origin = null;
    if (event.stage.kind === 'transit') {
      for (let prior = index - 1; prior >= 0; prior -= 1) {
        if (history[prior].stage.kind === 'transit') continue;
        const candidate = locationForEvent(history[prior], locationsById, journey.data.sample);
        if (coordinateFor(candidate) !== null) {
          origin = candidate;
          break;
        }
      }
    }
    return {
      event,
      location,
      origin,
      pending: index === history.length - 1 && event.stage.kind === 'transit',
    };
  }, [journey.data, locationsById, visibleCard]);
  const labelItems = useMemo(() => {
    const items = [];
    if (pendingDestination !== null) {
      const title = tx('ui.samples.heading_to_title_039e3b47', { title: pendingDestination.copy.title });
      items.push({
        key: `pending-${pendingDestination.event.id}`,
        coordinates: pendingDestination.coordinates,
        width: Math.min(186, Math.max(title.length * 5.4, pendingDestination.copy.detail.length * 4.5) + 10) / layoutScale,
        height: 23 / layoutScale,
        clearance: 11 / layoutScale,
      });
    }
    markers.forEach((marker) => {
      const copy = mapCheckpointText(marker);
      items.push({
        key: `checkpoint-${marker.event.id}`,
        coordinates: marker.coordinates,
        width: Math.min(174, Math.max(copy.title.length * 5.4, copy.detail.length * 4.5) + 8) / layoutScale,
        height: 23 / layoutScale,
        clearance: 11 / layoutScale,
        obstacleWidth: 15 / layoutScale,
        offsetX: marker.markerOffset / layoutScale,
        sideFirst: true,
      });
    });
    legs.filter((leg) => leg.event.stage.kind === 'transit').forEach((leg) => {
      const width = Math.max(74, Math.min(156, 28 + codeLabel('sampleStage', leg.event.stage.code).length * 5.7));
      items.push({
        key: `transit-${leg.event.id}`,
        coordinates: leg.midpoint,
        width: width / layoutScale,
        height: 20 / layoutScale,
        clearance: 3 / layoutScale,
        centered: true,
      });
    });
    return items;
  }, [pendingDestination, markers, legs, layoutScale]);
  const labelPlacements = useMemo(
    () => placeMapLabels(labelItems, activeView),
    [labelItems, activeView],
  );
  const panelPosition = useMemo(() => {
    if (visibleCard === null || viewportSize.width <= 0 || viewportSize.height <= 0) return null;
    const eventId = Number(visibleCard.eventId);
    const point = points.find((candidate) => Number(candidate.event.id) === eventId);
    if (point === undefined) return null;

    let coordinates = point.coordinates;
    let offsetX = 0;
    let offsetY = 0;
    const anchor = visibleCard.anchor
      ?? (point.event.stage.kind === 'transit' ? 'transit' : 'checkpoint');
    if (anchor === 'destination' && pendingDestination !== null) {
      coordinates = pendingDestination.coordinates;
    } else if (anchor === 'transit') {
      const leg = legs.find((candidate) => Number(candidate.event.id) === eventId);
      if (leg !== undefined) {
        coordinates = leg.midpoint;
        const placement = labelPlacements.get(`transit-${leg.event.id}`);
        offsetX = placement?.dx ?? 0;
        offsetY = placement?.dy ?? 0;
      }
    } else {
      const marker = markers.find((candidate) => Number(candidate.event.id) === eventId);
      if (marker !== undefined) offsetX = marker.markerOffset / layoutScale;
    }

    const projected = projectMapCoordinate(coordinates, activeView);
    const scale = viewportSize.scale;
    const mapLeft = (viewportSize.width - 900 * scale) / 2;
    const mapTop = (viewportSize.height - 520 * scale) / 2;
    const targetX = mapLeft + (projected[0] + offsetX) * scale;
    const targetY = mapTop + (projected[1] + offsetY) * scale;
    if (targetX < -12 || targetX > viewportSize.width + 12
        || targetY < -12 || targetY > viewportSize.height + 12) return null;
    return floatingPanelPosition(
      viewportSize,
      targetX,
      targetY,
    );
  }, [
    visibleCard, viewportSize, points, pendingDestination, legs, markers,
    labelPlacements, layoutScale, activeView,
  ]);
  const handleMapMoveStart = useCallback(() => {
    if (animationRef.current !== null) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
    setHoverCard(null);
  }, []);
  const handleMapMove = useCallback((position) => {
    setMapPosition((current) => viewFromTransform(position, current?.center ?? view.center));
  }, [view]);
  const handleMapMoveEnd = useCallback(({ coordinates, zoom }) => {
    const next = {
      center: Array.isArray(coordinates) ? coordinates : view.center,
      zoom,
    };
    setMapPosition(next);
    setFocusedView(next);
  }, [view]);

  if (journey.loading && journey.data === null) return <Loading label={tx("ui.samples.plotting_sample_movement_6dc6e535")} />;
  if (journey.error && journey.data === null) {
    return <Failed message={journey.error} onRetry={journey.refresh} />;
  }
  if (journey.data === null) return <Loading label={tx("ui.samples.plotting_sample_movement_6dc6e535")} />;

  const { sample, history: events } = journey.data;
  const activeEventId = hoverDetail?.event.id ?? null;
  const selectedEventId = selectedCard?.eventId ?? null;
  const orderedMarkers = [...markers].sort((left, right) => (
    Number(Number(left.event.id) === Number(selectedEventId))
    - Number(Number(right.event.id) === Number(selectedEventId))
  ));

  function animateMapTo(next) {
    animateMapBetween(activeView, next);
  }

  function focusMapTarget(eventId, anchor) {
    const point = points.find((candidate) => Number(candidate.event.id) === Number(eventId));
    if (point === undefined) return;
    let coordinates = point.coordinates;
    if (anchor === 'destination' && pendingDestination !== null) {
      coordinates = pendingDestination.coordinates;
    } else if (anchor === 'transit') {
      const leg = legs.find((candidate) => Number(candidate.event.id) === Number(eventId));
      if (leg !== undefined) coordinates = leg.midpoint;
    }
    const zoom = Math.min(32, Math.max(activeView.zoom + 0.75, activeView.zoom * 1.35));
    animateMapTo(focusedMapView(coordinates, zoom));
  }

  function clearMapSelection() {
    if (animationRef.current !== null) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setHoverCard(null);
    setSelectedCard(null);
  }

  function showMapDetail(eventId, anchor, pinned = false) {
    const card = { eventId: Number(eventId), anchor };
    if (pinned) {
      setHoverCard(null);
      if (Number(selectedCard?.eventId) === Number(eventId)
          && selectedCard?.anchor === anchor) {
        clearMapSelection();
        return;
      }
      setSelectedCard(card);
      focusMapTarget(eventId, anchor);
    } else setHoverCard(card);
  }

  function selectJourneyEvent(eventId) {
    const point = points.find((candidate) => Number(candidate.event.id) === Number(eventId));
    if (point === undefined) return;
    const anchor = point.event.stage.kind === 'transit' ? 'transit' : 'checkpoint';
    showMapDetail(eventId, anchor, true);
  }

  return (
    <section className="xo-sample-map" aria-busy={mapBusy}
      aria-label={tx("ui.samples.movement_history_for_activationcode_401d317d", { activationCode: sample.printedCode ?? sample.code })}>
      <div className="xo-sample-map-heading">
        <span className="xo-sample-map-mark">
          {sample.product?.mainImageThumbnailUrl
            ? <img src={sample.product?.mainImageThumbnailUrl} alt="" width="34" height="34" />
            : <Package aria-hidden="true" />}
        </span>
        <div>
          <strong>{sample.printedCode ?? sample.code}</strong>
          <span>{sample.product === null ? sample.template.name : sample.product.name}</span>
        </div>
        <span className="xo-sample-map-count">{events.length} {events.length === 1 ? tx("ui.samples.event_5006ed02") : tx("ui.samples.events_82d50d90")}</span>
      </div>

      <div className="xo-sample-map-canvas">
        <div className="xo-sample-map-viewport" ref={viewportRef}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 145 }}
            width={900}
            height={520}
            role="img"
            aria-label={tx("ui.samples.map_of_recorded_sample_locations_18c147d5")}
          >
            <ZoomableGroup
              center={focusedView?.center ?? view.center}
              zoom={focusedView?.zoom ?? view.zoom}
              minZoom={1}
              maxZoom={32}
              onMoveStart={handleMapMoveStart}
              onMove={handleMapMove}
              onMoveEnd={handleMapMoveEnd}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map((geography) => (
                  <Geography
                    key={geography.rsmKey}
                    geography={geography}
                    fill="#e7ebf3"
                    stroke="#ffffff"
                    strokeWidth={0.7 / visualScale}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: '#dde3ed' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))}
              </Geographies>

              {locations.filter((location) => location.isActive).map((location) => {
                const coordinates = coordinateFor(location);
                if (coordinates === null || markers.some((marker) => (
                  sameCoordinates(marker.coordinates, coordinates)
                )) || (pendingDestination !== null
                  && sameCoordinates(pendingDestination.coordinates, coordinates))) {
                  return null;
                }
                return (
                  <Marker key={`directory-${location.id}`} coordinates={coordinates}>
                    <circle r={1.6 / visualScale} fill="#8b93ad" opacity=".58" />
                  </Marker>
                );
              })}

              {legs.map((leg) => {
                const pending = pendingDestination !== null
                  && Number(pendingDestination.event.id) === Number(leg.event.id);
                return (
                  <Line
                    key={`leg-${leg.event.id}`}
                    coordinates={leg.route}
                    stroke={pending ? '#aeb5c2' : '#8aa3d2'}
                    strokeWidth={(pending ? 2.2 : 2.6) / visualScale}
                    strokeOpacity={pending ? 0.48 : 0.72}
                    strokeLinecap="round"
                    strokeDasharray={`${0.55 / visualScale} ${3.1 / visualScale}`}
                  />
                );
              })}

              {legs.filter((leg) => leg.event.stage.kind === 'transit').map((leg) => {
                const label = codeLabel('sampleStage', leg.event.stage.code);
                const width = Math.max(74, Math.min(156, 28 + label.length * 5.7));
                const pending = pendingDestination !== null
                  && Number(pendingDestination.event.id) === Number(leg.event.id);
                const selected = Number(activeEventId) === Number(leg.event.id);
                const placement = labelPlacements.get(`transit-${leg.event.id}`) ?? { dx: 0, dy: 0 };
                return (
                  <Marker
                    key={`transit-${leg.event.id}`}
                    className="xo-sample-map-target"
                    coordinates={leg.midpoint}
                    role="button"
                    tabIndex={0}
                    aria-label={tx("ui.samples.show_details_for_label_60929055", { label: label })}
                    onMouseEnter={() => showMapDetail(leg.event.id, 'transit')}
                    onMouseLeave={() => setHoverCard(null)}
                    onFocus={() => showMapDetail(leg.event.id, 'transit')}
                    onBlur={() => setHoverCard(null)}
                    onClick={() => showMapDetail(leg.event.id, 'transit', true)}
                  >
                    <g transform={`translate(${placement.dx / mapZoom} ${placement.dy / mapZoom})`}>
                      <rect
                        x={-width / 2 / visualScale}
                        y={-10 / visualScale}
                        width={width / visualScale}
                        height={20 / visualScale}
                        rx={5 / visualScale}
                        fill={selected ? 'rgba(29,107,251,.07)' : 'transparent'}
                      />
                      <text
                        textAnchor="middle"
                        y={3.2 / visualScale}
                        fill={selected ? '#1d6bfb' : pending ? '#717988' : '#365791'}
                        fontSize={9.6 / visualScale}
                        fontWeight="700"
                        paintOrder="stroke"
                        stroke="#ffffff"
                        strokeWidth={3.2 / visualScale}
                      >
                        {leg.sequence} · {label}
                      </text>
                    </g>
                  </Marker>
                );
              })}

              {pendingDestination !== null && (() => {
                const placement = labelPlacements.get(`pending-${pendingDestination.event.id}`)
                  ?? { dx: 0, dy: 16 };
                const selected = Number(activeEventId) === Number(pendingDestination.event.id);
                return (
                  <Marker
                    key={`pending-${pendingDestination.event.id}`}
                    className="xo-sample-map-target"
                    coordinates={pendingDestination.coordinates}
                    role="button"
                    tabIndex={0}
                    aria-label={tx("ui.samples.show_pending_destination_title_062c59fa", { title: pendingDestination.copy.title })}
                    onMouseEnter={() => showMapDetail(pendingDestination.event.id, 'destination')}
                    onMouseLeave={() => setHoverCard(null)}
                    onFocus={() => showMapDetail(pendingDestination.event.id, 'destination')}
                    onBlur={() => setHoverCard(null)}
                    onClick={() => showMapDetail(pendingDestination.event.id, 'destination', true)}
                  >
                    {selected && (
                      <circle
                        r={10 / visualScale}
                        fill="rgba(29,107,251,.08)"
                        stroke="#8a96ad"
                        strokeWidth={0.8 / visualScale}
                      />
                    )}
                    <circle
                      r={6.5 / visualScale}
                      fill="#f7f8fb"
                      stroke="#929aa9"
                      strokeWidth={1.2 / visualScale}
                      strokeDasharray={`${0.7 / visualScale} ${1.8 / visualScale}`}
                    />
                    <circle r={1.9 / visualScale} fill="#929aa9" />
                    <g transform={`translate(${placement.dx / mapZoom} ${placement.dy / mapZoom})`}>
                      <text
                        textAnchor={placement.textAnchor ?? 'middle'}
                        x={(placement.textX ?? 0) / mapZoom}
                        y={-2.5 / visualScale}
                        fill="#59647d"
                        fontSize={10.5 / visualScale}
                        fontWeight="700"
                        paintOrder="stroke"
                        stroke="#ffffff"
                        strokeWidth={2.8 / visualScale}
                      >{tx("ui.samples.heading_to_title_039e3b47", { title: pendingDestination.copy.title })}</text>
                      <text
                        textAnchor={placement.textAnchor ?? 'middle'}
                        x={(placement.textX ?? 0) / mapZoom}
                        y={8 / visualScale}
                        fill="#7a849b"
                        fontSize={8.5 / visualScale}
                        fontWeight="600"
                        paintOrder="stroke"
                        stroke="#ffffff"
                        strokeWidth={2.3 / visualScale}
                      >
                        {pendingDestination.copy.detail}
                      </text>
                    </g>
                  </Marker>
                );
              })()}

              {orderedMarkers.map((marker) => {
                const copy = mapCheckpointText(marker);
                const placement = labelPlacements.get(`checkpoint-${marker.event.id}`)
                  ?? { dx: 18, dy: 0 };
                const pinned = Number(selectedEventId) === Number(marker.event.id);
                const selected = pinned || Number(activeEventId) === Number(marker.event.id);
                const muted = selectedEventId !== null && !pinned;
                return (
                  <Marker
                    key={`checkpoint-${marker.event.id}`}
                    coordinates={marker.coordinates}
                  >
                    <g
                      className={`xo-sample-map-target${muted ? ' is-muted' : ''}`}
                      transform={`translate(${marker.markerOffset / visualScale} 0)`}
                      role="button"
                      tabIndex={0}
                      aria-label={tx("ui.samples.show_label_at_title_174cc4cd", { label: codeLabel('sampleStage', marker.event.stage.code), title: copy.title })}
                      onMouseEnter={() => showMapDetail(marker.event.id, 'checkpoint')}
                      onMouseLeave={() => setHoverCard(null)}
                      onFocus={() => showMapDetail(marker.event.id, 'checkpoint')}
                      onBlur={() => setHoverCard(null)}
                      onClick={() => showMapDetail(marker.event.id, 'checkpoint', true)}
                    >
                      {selected && (
                        <circle
                          r={10 / visualScale}
                          fill="rgba(29,107,251,.12)"
                          stroke="#1d6bfb"
                          strokeWidth={0.9 / visualScale}
                        />
                      )}
                      <circle
                        r={6.5 / visualScale}
                        fill={selected ? '#1d6bfb' : '#203a85'}
                        stroke="#ffffff"
                        strokeWidth={1.35 / visualScale}
                      />
                      <text
                        textAnchor="middle"
                        y={2.3 / visualScale}
                        fill="#ffffff"
                        fontSize={6.6 / visualScale}
                        fontWeight="700"
                      >
                        {marker.sequence}
                      </text>
                    </g>
                    <g transform={`translate(${marker.markerOffset / visualScale + placement.dx / mapZoom} ${placement.dy / mapZoom})`}>
                      <text
                        textAnchor={placement.textAnchor ?? 'middle'}
                        x={(placement.textX ?? 0) / mapZoom}
                        y={-2.5 / visualScale}
                        fill="#10214b"
                        fontSize={10.5 / visualScale}
                        fontWeight="700"
                        paintOrder="stroke"
                        stroke="#ffffff"
                        strokeWidth={2.8 / visualScale}
                      >
                        {copy.title}
                      </text>
                      <text
                        textAnchor={placement.textAnchor ?? 'middle'}
                        x={(placement.textX ?? 0) / mapZoom}
                        y={8 / visualScale}
                        fill="#66718d"
                        fontSize={8.5 / visualScale}
                        fontWeight="600"
                        paintOrder="stroke"
                        stroke="#ffffff"
                        strokeWidth={2.3 / visualScale}
                      >
                        {copy.detail}
                      </text>
                    </g>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>

          {points.length === 0 && (
            <div className="xo-sample-map-empty">
              <MapPin aria-hidden="true" />
              <strong>{tx("ui.samples.no_mapped_checkpoint_yet_7f2a627b")}</strong>
              <span>{tx("ui.samples.movement_appears_here_as_locations_are_recorded_59817b40")}</span>
            </div>
          )}
          <span className="xo-sample-map-hint">{tx("ui.samples.drag_to_move_scroll_to_zoom_f9b0aeaa")}</span>
          <JourneyDetailPanel
            key={`${visibleCard?.eventId ?? 'none'}:${visibleCard?.anchor ?? 'none'}`}
            detail={hoverDetail}
            position={panelPosition}
            onClose={clearMapSelection}
          />
        </div>
      </div>

      <ol className="xo-sample-route-strip" aria-label={tx("ui.samples.recorded_journey_3a012c3d")}>
        {events.map((event, index) => {
          const location = locationForEvent(event, locationsById, sample);
          return (
            <li key={event.id} className={Number(activeEventId) === Number(event.id) ? 'is-selected' : ''}>
              <button type="button" onClick={() => selectJourneyEvent(event.id)}>
                <span>{index + 1}</span>
                <div>
                  <strong>{codeLabel('sampleStage', event.stage.code)}</strong>
                  <small>{locationLabel(location)} · {when(event.occurredAt)}</small>
                </div>
              </button>
            </li>
          );
        })}
      </ol>
      {mapBusy && (
        <div className="xo-sample-map-loading" role="status">
          <Loader2 aria-hidden="true" /><span>{tx("ui.samples.loading_sample_journey_9fa9ecfb")}</span>
        </div>
      )}
      {!mapBusy && journey.error && (
        <div className="xo-sample-map-loading is-error" role="alert">
          <span>{journey.error}</span>
          <button type="button" className="xo-btn-2" onClick={journey.refresh}>{tx("ui.samples.try_again_042c862e")}</button>
        </div>
      )}
    </section>
  );
}

function MovementRecorder({ sample, stage, locations, onDone }) {
  const [locationId, setLocationId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locationKind = requiredLocationKind(stage);
  const choices = locations.filter((location) => (
    location.isActive && location.kind === locationKind
  ));

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const step = { stageCode: stage.code };
      if (locationKind !== null) step.locationId = Number(locationId);
      if (note.trim() !== '') step.note = note.trim();
      await recordSampleStep(sample.uuid, step);
      setNote('');
      await onDone(tx("ui.samples.recorded_label_c1021fa3", { label: codeLabel('sampleStage', stage.code) }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="xo-sample-selected-action">
      <header>
        <span className={`t-${toneOf(stage.kind)}`}><Check aria-hidden="true" /></span>
        <div><small>{tx("ui.samples.record_selected_step_41fd7303")}</small><h3>{codeLabel('sampleStage', stage.code)}</h3><p>{tx("ui.samples.add_the_details_that_confirm_this_checkpoint_a0ece787")}</p></div>
      </header>
      <form className="xo-sample-step-details" onSubmit={submit}>
        <div className="xo-sample-step-fields">
          {locationKind !== null && (
            <Field id="sample-location" label={locationKind === 'laboratory' ? tx("ui.samples.laboratory_e376f958") : tx("ui.samples.office_6c3a72ea")}>
              <XomicsSelect {...fieldAria('sample-location')} value={locationId} disabled={busy}
                onChange={(event) => setLocationId(event.target.value)}>
                <option value="">{tx("ui.samples.choose_a_location_9e520806")}</option>
                {choices.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} · {location.code}
                  </option>
                ))}
              </XomicsSelect>
            </Field>
          )}
          <Field id="sample-note" label={tx("ui.samples.internal_note_e6e5bd84")} hint={tx("ui.samples.optional_0c6c4102")}>
            <input type="text" {...fieldAria('sample-note', { hint: 'x' })} value={note} disabled={busy}
              maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </div>
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <footer className="xo-sample-flow-footer">
          <button type="submit" className="xo-btn"
            disabled={busy || (locationKind !== null && locationId === '')}>
            {busy ? tx("ui.samples.recording_72f9eb46") : tx("ui.samples.record_checkpoint_263400c3")}
          </button>
        </footer>
      </form>
    </section>
  );
}

function Capability({ yes, children }) {
  return (
    <span className={yes ? 'is-supported' : 'is-unsupported'}>
      {yes ? <Check aria-hidden="true" /> : <XCircle aria-hidden="true" />}{children}
    </span>
  );
}

function ShipmentComposer({ sample, stage, locations, onDone }) {
  const [destinationId, setDestinationId] = useState('');
  const [weightGrams, setWeightGrams] = useState(() => sample.shippingWeightGrams === null ? '' : String(sample.shippingWeightGrams));
  const [quoteSet, setQuoteSet] = useState(null);
  const [quoteId, setQuoteId] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const quoteRequestRef = useRef(0);
  const providerResultsRef = useRef(null);

  const destinationKind = normalizeDestinationKind(stage.destinationKind);
  const destinations = locations.filter((location) => {
    if (!location.isActive || Number(location.id) === Number(sample.at?.id)) return false;
    return location.kind === destinationKind;
  });
  const sourceAvailable = sample.at !== null && sample.currentStage?.kind !== 'transit';

  async function quote(nextDestinationId) {
    const request = quoteRequestRef.current + 1;
    quoteRequestRef.current = request;
    setBusy(true);
    setError('');
    setReceipt(null);
    try {
      const result = await loadCarrierQuotes({
        subjectType: 'sample',
        subjectUuid: sample.uuid,
        stageCode: stage.code,
        destinationLocationId: Number(nextDestinationId),
        weightGrams: Number(weightGrams),
      });
      if (request !== quoteRequestRef.current) return;
      setQuoteSet(result);
      setQuoteId(result.quotes[0]?.quoteId ?? '');
    } catch (requestError) {
      if (request === quoteRequestRef.current) setError(errorMessage(requestError));
    } finally {
      if (request === quoteRequestRef.current) setBusy(false);
    }
  }

  useEffect(() => {
    if (quoteSet === null || providerResultsRef.current === null) return undefined;
    const frame = window.requestAnimationFrame(() => {
      providerResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [quoteSet]);

  async function purchase() {
    setBusy(true);
    setError('');
    try {
      const result = await purchaseCarrierLabel({
        subjectType: 'sample',
        subjectUuid: sample.uuid,
        stageCode: stage.code,
        destinationLocationId: Number(destinationId),
        weightGrams: Number(weightGrams),
        quoteId,
        note: note.trim() === '' ? null : note.trim(),
      });
      setReceipt(result);
      setQuoteSet(null);
      setQuoteId('');
      await onDone(tx('ui.samples.label_created_not_dispatched_f5973231'));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  const providers = quoteSet === null ? [] : quoteSet.providers;

  return (
    <section className="xo-sample-selected-action">
      <header>
        <span className="t-warn"><Truck aria-hidden="true" /></span>
        <div><small>{codeLabel('sampleStage', stage.code)}</small><h3>{tx("ui.samples.arrange_the_movement_66d5ce5f")}</h3><p>{tx("ui.samples.choose_the_destination_then_compare_the_carriers_avail_f0097d42")}</p></div>
      </header>

      {!sourceAvailable ? (
        <p className="xo-sample-guidance">
          {tx("ui.samples.a_carrier_label_needs_a_confirmed_custody_location_rec_fecc0c44")}
        </p>
      ) : (
        <div className="xo-sample-action-form">
          <Field id="collection-parcel-weight" label={tx('ui.samples.collection_parcel_weight')} hint={tx('ui.samples.collection_weight_hint')}>
            <input {...fieldAria('collection-parcel-weight', { hint: true })} type="number" min="1" step="1" value={weightGrams} disabled={busy}
              onChange={event => {
                quoteRequestRef.current += 1;
                setWeightGrams(event.target.value); setDestinationId(''); setQuoteSet(null); setQuoteId(''); setError('');
              }} />
          </Field>
          <Field id="shipment-destination" label={tx("ui.samples.send_sample_to_9e688568")}>
            <XomicsSelect {...fieldAria('shipment-destination')} value={destinationId} disabled={busy || !Number.isSafeInteger(Number(weightGrams)) || Number(weightGrams) < 1}
              onChange={(event) => {
                const nextDestinationId = event.target.value;
                setDestinationId(nextDestinationId);
                setQuoteSet(null);
                setQuoteId('');
                setReceipt(null);
                setError('');
                if (nextDestinationId === '') {
                  quoteRequestRef.current += 1;
                  setBusy(false);
                } else {
                  void quote(nextDestinationId);
                }
              }}>
              <option value="">{tx("ui.samples.choose_any_office_or_laboratory_6a006762")}</option>
              {destinations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name} · {location.kind === 'laboratory' ? tx("ui.samples.laboratory_e376f958") : tx("ui.samples.office_6c3a72ea")} · {location.address?.city}
                </option>
              ))}
            </XomicsSelect>
          </Field>
          <Field id="shipment-note" label={tx("ui.samples.shipment_note_cf2ec0f4")} hint={tx("ui.samples.optional_0c6c4102")}>
            <input type="text" {...fieldAria('shipment-note', { hint: 'x' })} value={note} disabled={busy}
              maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
          {busy && quoteSet === null && destinationId !== '' && (
            <p className="xo-sample-quote-loading" role="status">
              <Loader2 aria-hidden="true" /> {tx("ui.samples.checking_available_carriers_32e870d8")}
            </p>
          )}

          {quoteSet !== null && (
            <div className="xo-sample-provider-results" ref={providerResultsRef}>
              <div className="xo-sample-route-summary">
                <Route aria-hidden="true" />
                <strong>{quoteSet.fromLocation.code} → {quoteSet.destination.code}</strong>
                <span>{quoteSet.parcel.weightGrams.toLocaleString()} g</span>
              </div>
              {providers.map((provider) => {
                const quotes = quoteSet.quotes.filter(
                  (candidate) => Number(candidate.carrierId) === Number(provider.carrierId),
                );
                return (
                  <article className={`xo-sample-provider${provider.routeAvailable ? '' : ' is-unavailable'}`}
                    key={provider.carrierId}>
                    <header>
                      <div><strong>{provider.carrierName}</strong><span>{provider.carrierCode}</span></div>
                      {quotes.length > 0 && <span>{quotes.length} {quotes.length === 1 ? tx("ui.samples.service_4cf5bc59") : tx("ui.samples.services_3e7aaa79")}</span>}
                    </header>
                    <div className="xo-sample-capabilities">
                      <Capability yes={provider.availableAtLocation}>{tx("ui.samples.available_at_code_07b12a5c", { code: quoteSet.fromLocation.code })}</Capability>
                      <Capability yes={provider.supportsInternational}>{tx("ui.samples.international_bc440f64")}</Capability>
                    </div>
                    {quotes.length === 0 ? (
                      <p className="xo-sample-provider-unavailable">
                        {provider.unavailableReason ?? tx("ui.samples.no_service_is_available_for_this_route_961a5820")}
                      </p>
                    ) : quotes.map((option) => (
                      <label className={`xo-sample-quote${quoteId === option.quoteId ? ' is-selected' : ''}`}
                        key={option.quoteId}>
                        <input type="radio" name="sample-quote" value={option.quoteId}
                          checked={quoteId === option.quoteId} disabled={busy}
                          onChange={() => setQuoteId(option.quoteId)} />
                        <span><strong>{option.serviceName}</strong><small>{tx("ui.samples.estimateddaysmin_estimateddaysmax_working_days_5be9163a", { estimatedDaysMin: option.estimatedDaysMin, estimatedDaysMax: option.estimatedDaysMax })}</small></span>
                        <strong>{money(option.totalCostMinor, option.currency)}</strong>
                      </label>
                    ))}
                  </article>
                );
              })}
              {providers.length === 0 && (
                <p className="xo-sample-guidance">{tx("ui.samples.no_carrier_is_configured_for_this_source_location_b76cf864")}</p>
              )}
              <button type="button" className="xo-btn" disabled={busy || quoteId === ''} onClick={purchase}>
                <Send aria-hidden="true" /> {busy ? tx("ui.samples.generating_label_f8a394a1") : tx("ui.samples.generate_label_3352ce5b")}
              </button>
            </div>
          )}

          {receipt !== null && (
            <div className="xo-sample-label-receipt" role="status">
              <CircleCheck aria-hidden="true" />
              <div>
                <strong>{tx("ui.samples.label_created_not_dispatched_f5973231")}</strong>
                <span>{receipt.shipment.carrier.name} · {receipt.labels.outbound.trackingNumber}</span>
                <ShippingLabelButton labelUrl={receipt.labels.outbound.labelUrl} className="xo-sample-label-action" printable>
                  {tx('ui.samples.download_label')}
                </ShippingLabelButton>
                {receipt.labels.outbound.invoiceUrl && (
                  <ShippingLabelButton labelUrl={receipt.labels.outbound.invoiceUrl} className="xo-sample-label-action">
                    {tx('ui.carrier.customs_invoice')}
                  </ShippingLabelButton>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PendingSampleShipment({ shipment, mayDispatch, onDone }) {
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState('');

  async function dispatch() {
    setDispatching(true);
    setError('');
    try {
      await dispatchCarrierShipment(shipment.uuid);
      await onDone(tx('ui.samples.shipment_dispatched'));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setDispatching(false);
    }
  }

  return (
    <div className="xo-sample-pending-shipment">
      <header>
        <span><Truck aria-hidden="true" /></span>
        <div>
          <small>{tx('ui.samples.label_created_not_dispatched_f5973231')}</small>
          <strong>{codeLabel('sampleStage', shipment.stage.code)}</strong>
          <p>{shipment.carrier.name} · {shipment.trackingNumber}</p>
        </div>
      </header>
      <dl>
        <div><dt>{tx('ui.samples.destination_d4271349')}</dt><dd>{shipment.destination.name}</dd></div>
        <div><dt>{tx('ui.shipments.status')}</dt><dd>{readableCode(shipment.status)}</dd></div>
      </dl>
      {shipment.note && <p className="xo-sample-pending-note">{shipment.note}</p>}
      <footer>
        <ShippingLabelButton labelUrl={shipment.labelUrl} className="xo-sample-label-action" printable>
          {tx('ui.samples.download_shipping_label_d223f1ce')}
        </ShippingLabelButton>
        {mayDispatch && (
          <button type="button" className="xo-btn" disabled={dispatching} onClick={dispatch}>
            {dispatching ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
            {dispatching ? tx('ui.carrier.dispatching') : tx('ui.carrier.mark_dispatched')}
          </button>
        )}
      </footer>
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    </div>
  );
}

function CurrentSampleState({ sample, history, locationsById }) {
  const latest = history[history.length - 1] ?? null;
  const stage = latest?.stage ?? sample.currentStage;
  const place = latest === null
    ? activationLocation(sample)
    : locationForEvent(latest, locationsById, sample);
  const placeCopy = place === null ? null : mapLocationText(place);
  const address = place === null ? null : mapLocationAddress(place);
  const transit = stage?.kind === 'transit';
  const activation = stage?.code === 'activated';
  const position = placeCopy === null
    ? tx('ui.samples.no_physical_checkpoint_recorded')
    : transit
      ? tx('ui.samples.heading_to_title_039e3b47', { title: placeCopy.title })
      : tx('ui.samples.at_location', { location: placeCopy.title });

  return (
    <section className="xo-sample-current-state">
      <header>
        <span className={`t-${toneOf(stage?.kind)}`}>
          {transit ? <Truck aria-hidden="true" /> : <MapPin aria-hidden="true" />}
        </span>
        <div>
          <small>{tx("ui.samples.current_step_be1ac4e3")}</small>
          <h3>{stage?.label ?? tx("ui.samples.journey_not_started_d92b6844")}</h3>
          <p>{position}{activation && place?.demo && <span className="xo-sample-demo-tag">{tx('ui.samples.demo_activation')}</span>}</p>
        </div>
      </header>
      <dl>
        {!activation && <div className="is-wide">
          <dt>{tx("ui.samples.position_details_2f37c8c3")}</dt>
          <dd><strong>{position}</strong>{placeCopy?.detail && <span>{placeCopy.detail}</span>}{address && <small>{address}</small>}</dd>
        </div>}
        <div><dt>{tx("ui.samples.recorded_d5383ea7")}</dt><dd><strong>{when(latest?.occurredAt ?? sample.activatedAt)}</strong></dd></div>
        {!activation && <div><dt>{tx("ui.samples.responsible_96cd625f")}</dt><dd><strong>{actorLabel(stage?.actor ?? 'system')}</strong><span>{readableCode(stage?.kind ?? 'activation')}</span></dd></div>}
        {latest?.shipment && (
          <div className="is-wide">
            <dt>{tx("ui.samples.shipment_0cd0f284")}</dt>
            <dd>
              <strong>{latest.shipment.carrier?.name ?? latest.shipment.carrier?.code ?? tx("ui.samples.managed_shipment_acdb55fd")}</strong>
              <span>{latest.shipment.trackingNumber ?? tx("ui.samples.tracking_pending_5455cb5a")} · {readableCode(latest.shipment.status)}</span>
            </dd>
          </div>
        )}
        {latest?.by && (
          <div className={activation ? undefined : 'is-wide'}><dt>{tx("ui.samples.recorded_by_664aa757")}</dt><dd><strong>{latest.by.name || latest.by.email}</strong></dd></div>
        )}
      </dl>
      {latest?.note && !isTestActivation(latest) && <p className="xo-sample-current-note">{latest.note}</p>}
    </section>
  );
}

function NextStepFlow({
  sample, stages, locations, mayWrite, mayShip, mayUpload, onDone, onOpenUpload,
}) {
  const [selectedCode, setSelectedCode] = useState(null);
  const controlsRef = useRef(null);
  const currentOrder = Number(sample.currentStage?.sortOrder ?? -1);
  const candidates = sample.pendingShipment !== null || sample.currentStage?.isTerminal
    ? [] : stages.filter((stage) => Number(stage.sortOrder) > currentOrder);
  const selected = candidates.find((stage) => stage.code === selectedCode) ?? null;

  useEffect(() => { setSelectedCode(null); }, [
    sample.uuid, sample.currentStage?.id, sample.pendingShipment?.uuid,
  ]);
  useEffect(() => {
    if (selected === null || controlsRef.current === null) return;
    const frame = window.requestAnimationFrame(() => {
      controlsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selected]);

  return (
    <section className="xo-sample-next-flow">
      <header><div><small>{tx("ui.samples.next_steps_11fc1420")}</small><h3>{tx("ui.samples.what_happens_next_4ef9217b")}</h3><p>{tx("ui.samples.select_the_movement_or_checkpoint_you_are_recording_no_def0cbe9")}</p></div></header>
      {sample.pendingShipment !== null ? (
        <PendingSampleShipment
          shipment={sample.pendingShipment}
          mayDispatch={mayShip && mayWrite}
          onDone={onDone}
        />
      ) : candidates.length === 0 ? (
        <p className="xo-sample-guidance">{tx("ui.samples.this_journey_has_reached_its_final_configured_step_e3d5642a")}</p>
      ) : (
        <div className="xo-sample-stage-tiles" aria-label={tx("ui.samples.available_next_steps_a25672e7")}>
          {candidates.map((candidate) => {
            const uploadShortcut = candidate.code === 'uploaded';
            const active = candidate.code === selectedCode;
            const recommended = candidate.code === sample.next?.code;
            const uploadUnavailable = !mayUpload || sample.patientId === null
              || sample.pipelines.length === 0
              || !sample.pipelines.some(p => p.available);
            let uploadDetail = tx('ui.samples.open_uploads_prepared');
            if (!mayUpload) uploadDetail = tx("ui.samples.you_do_not_have_permission_to_create_uploads_767ef7c7");
            else if (sample.patientId === null) uploadDetail = tx("ui.samples.this_kit_has_no_patient_linked_fdca791a");
            else if (sample.pipelines.length === 0) uploadDetail = tx("ui.samples.this_kit_type_has_no_pipeline_selected_739c0f1f");
            else if (!sample.pipelines.some(p => p.available)) uploadDetail = tx("ui.samples.the_chosen_pipeline_is_no_longer_active_b6c099b4");
            return (
              <button type="button" role={uploadShortcut ? undefined : 'radio'}
                aria-checked={uploadShortcut ? undefined : active} key={candidate.code}
                disabled={uploadShortcut && uploadUnavailable}
                className={`${active ? 'is-selected' : ''}${recommended && !uploadShortcut ? ' is-recommended' : ''}${uploadShortcut ? ' is-shortcut' : ''}`}
                onClick={() => {
                  if (uploadShortcut) onOpenUpload(sample);
                  else setSelectedCode(active ? null : candidate.code);
                }}>
                <span className={`t-${toneOf(candidate.kind)}`}>
                  {uploadShortcut ? <UploadCloud aria-hidden="true" />
                    : candidate.kind === 'transit' ? <Truck aria-hidden="true" /> : <Check aria-hidden="true" />}
                </span>
                <span>
                  <strong>{uploadShortcut ? tx("ui.samples.upload_sample_data_c11f1dac") : codeLabel('sampleStage', candidate.code)}</strong>
                  <small>{uploadShortcut ? uploadDetail : tx("ui.samples.value_value2_64684f24", { value: readableCode(candidate.kind), value2: actorLabel(candidate.actor) })}</small>
                </span>
                {uploadShortcut ? <ChevronRight className="xo-sample-stage-shortcut-arrow" aria-hidden="true" />
                  : recommended && <em>{tx("ui.samples.recommended_9ef93755")}</em>}
              </button>
            );
          })}
        </div>
      )}

      {selected !== null && (
        <div className="xo-sample-next-controls" ref={controlsRef}>
          {selected.kind === 'transit' ? (
            mayShip
              ? <ShipmentComposer key={selected.code} sample={sample} stage={selected} locations={locations} onDone={onDone} />
              : <p className="xo-sample-guidance">{tx("ui.samples.you_do_not_have_permission_to_create_this_shipment_02f3e5c5")}</p>
          ) : (
            mayWrite
              ? <MovementRecorder key={selected.code} sample={sample} stage={selected} locations={locations} onDone={onDone} />
              : <p className="xo-sample-guidance">{tx("ui.samples.you_do_not_have_permission_to_record_this_checkpoint_01afea88")}</p>
          )}
        </div>
      )}
    </section>
  );
}

function SampleDetailModal({
  uuid, stages, locations, mayWrite, mayShip, mayReadShip, mayUpload, onClose, onChanged,
  onOpenShipment, onOpenUpload,
}) {
  const load = useCallback(() => loadSample(uuid), [uuid]);
  const detail = useLiveResource(['samples'], load);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  async function changed(message) {
    setNotice(message);
    await detail.refresh();
    onChanged(message);
  }

  const sample = detail.data?.sample ?? null;
  const history = detail.data?.history ?? [];
  const locationsById = useMemo(
    () => new Map(locations.map((location) => [Number(location.id), location])),
    [locations],
  );

  return (
    <div className="xo-sample-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-sample-modal" role="dialog" aria-modal="true"
        aria-labelledby="sample-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div className="xo-sample-modal-title">
            <span>
              {sample?.product?.mainImageThumbnailUrl
                ? <img src={sample.product?.mainImageThumbnailUrl} alt="" width="42" height="42" />
                : <Package aria-hidden="true" />}
            </span>
            <div>
              <small>{tx("ui.samples.sample_journey_44b47700")}</small>
              <h2 id="sample-modal-title">{sample?.printedCode ?? sample?.code ?? tx("ui.samples.sample_details_422ae54c")}</h2>
              {sample !== null && <p>{sample.product === null ? sample.template.name : sample.product.name}</p>}
            </div>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.samples.close_sample_details_0621890f")} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>

        {detail.loading && detail.data === null && <Loading label={tx("ui.samples.reading_the_sample_journey_9880020b")} />}
        {detail.error && <Failed message={detail.error} onRetry={detail.refresh} />}

        {sample !== null && (
          <div className="xo-sample-modal-body">
            <div className="xo-sample-modal-main">
              {notice && <p className="xo-alert t-ok" role="status">{notice}</p>}
              <CurrentSampleState sample={sample} history={history} locationsById={locationsById} />
              <SampleOrganizationCodes organization={detail.data.organization} tubes={detail.data.tubes}
                mayRecord={detail.data.mayRecordOrganizationCodes} onSaved={detail.refresh} />
              <NextStepFlow sample={sample} stages={stages} locations={locations}
                mayWrite={mayWrite} mayShip={mayShip} mayUpload={mayUpload} onDone={changed}
                onOpenUpload={onOpenUpload} />
            </div>

            <aside className="xo-sample-history">
              <header><div><h3>{tx("ui.samples.movement_history_9418ba71")}</h3><span>{tx("ui.samples.every_recorded_checkpoint_dcc637b8")}</span></div><small>{tx("ui.samples.length_recorded_9cfa30a0", { length: history.length })}</small></header>
              {history.length === 0 ? (
                <Empty title={tx("ui.samples.no_movement_yet_9407ff42")} detail={tx("ui.samples.the_first_checkpoint_will_appear_here_3806b136")} />
              ) : (
                <ol>
                  {history.map((event) => {
                    const shipmentLabel = event.shipment?.uuid
                      ? `/Carrier/shipments/${encodeURIComponent(event.shipment.uuid)}/labels/outbound`
                      : null;
                    const checkpoint = locationForEvent(event, locationsById, sample);
                    const checkpointCopy = checkpoint === null ? null : mapLocationText(checkpoint);
                    const checkpointAddress = checkpoint === null ? null : mapLocationAddress(checkpoint);
                    const transit = event.stage.kind === 'transit';
                    const activation = event.stage.code === 'activated';
                    return (
                      <li key={event.id}>
                        <span className={`xo-sample-history-marker t-${toneOf(event.stage.kind)}`}>
                          {transit ? <Truck aria-hidden="true" /> : event.stage.kind === 'output'
                            ? <CircleCheck aria-hidden="true" /> : <MapPin aria-hidden="true" />}
                        </span>
                        <article>
                          <header>
                            <div><strong>{codeLabel('sampleStage', event.stage.code)}</strong>
                              {activation
                                ? checkpoint?.demo && <small>{tx('ui.samples.demo_activation')}</small>
                                : <small>{readableCode(event.stage.kind)} · {actorLabel(event.stage.actor)}</small>}
                            </div>
                            <time>{when(event.occurredAt)}</time>
                          </header>
                          {activation ? (
                            <ActivationHistoryDetails sample={sample} event={event} location={checkpoint} />
                          ) : <dl>
                            <div className="is-wide">
                              <dt>{transit ? tx("ui.samples.destination_d4271349") : tx("ui.samples.checkpoint_5cb9afc0")}</dt>
                              <dd>
                                <strong>{checkpointCopy === null ? tx("ui.samples.no_physical_location_recorded_1f3da57f") : transit ? tx("ui.samples.heading_to_title_039e3b47", { title: checkpointCopy.title }) : checkpointCopy.title}</strong>
                                {checkpointCopy?.detail && <span>{checkpointCopy.detail}</span>}
                                {checkpointAddress && <small>{checkpointAddress}</small>}
                              </dd>
                            </div>
                            {event.shipment && (
                              <div className="is-wide">
                                <dt>{tx("ui.samples.shipment_0cd0f284")}</dt>
                                <dd>
                                  <strong>{event.shipment.carrier?.name ?? event.shipment.carrier?.code ?? tx("ui.samples.managed_shipment_acdb55fd")}</strong>
                                  <span>{event.shipment.trackingNumber ?? tx("ui.samples.tracking_pending_5455cb5a")} · {readableCode(event.shipment.status)}</span>
                                </dd>
                              </div>
                            )}
                            {event.by && (
                              <div className="is-wide"><dt>{tx("ui.samples.recorded_by_664aa757")}</dt><dd><strong>{event.by.name || event.by.email}</strong><span>{event.by.email}</span></dd></div>
                            )}
                          </dl>}
                          {!activation && event.note && <p>{event.note}</p>}
                          {event.stage.code === 'lab_received' && <SampleLabProgress
                            workflows={detail.data.labWorkflows.filter(workflow => event.location !== null && workflow.laboratoryId === event.location.id)}
                          />}
                          {shipmentLabel !== null && mayReadShip && (
                            <div className="xo-sample-shipment-actions">
                              <button type="button" className="xo-link"
                                onClick={() => onOpenShipment(event.shipment.uuid)}>
                                <Truck aria-hidden="true" /> {tx('ui.samples.open_shipment')}
                              </button>
                              <ShippingLabelButton labelUrl={shipmentLabel} className="xo-sample-label-action" printable>{tx("ui.samples.download_shipping_label_d223f1ce")}</ShippingLabelButton>
                            </div>
                          )}
                        </article>
                      </li>
                    );
                  })}
                </ol>
              )}
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}

function RecentActivity({ activity, selected, onSelect, onOpen }) {
  if (activity.loading && activity.data === null) return <Loading label={tx("ui.samples.reading_recent_activity_d2db5da4")} />;
  if (activity.error && activity.data === null) {
    return <Failed message={activity.error} onRetry={activity.refresh} />;
  }
  const events = activity.data?.events ?? [];
  if (events.length === 0) {
    return <Empty title={tx("ui.samples.no_recent_activity_6a52f1d4")} detail={tx("ui.samples.try_another_search_scope_or_journey_step_654359dd")} />;
  }
  return (
    <div className="xo-sample-activity-list">
      {events.map((event) => {
        const imageUrl = event.sample.product?.mainImageThumbnailUrl ?? null;
        const place = event.location?.name ?? event.location?.code ?? tx('ui.samples.no_physical_checkpoint');
        return (
          <article className={`xo-sample-activity-row${selected === event.sample.uuid ? ' is-selected' : ''}`}
            key={event.id}>
            <button type="button" className="xo-sample-activity-select"
              onClick={() => onSelect(event.sample.uuid)}>
              <span className="xo-sample-activity-image">
                {imageUrl === null
                  ? <Package aria-hidden="true" />
                  : <img src={imageUrl} alt="" width="42" height="42" loading="lazy" decoding="async" />}
              </span>
              <span className="xo-sample-activity-copy">
                <span><strong>{codeLabel('sampleStage', event.stage.code)}</strong><time>{when(event.occurredAt)}</time></span>
                <span>{event.sample.printedCode ?? event.sample.code} · {event.sample.product === null ? event.sample.template.name : event.sample.product.name}</span>
                <small>
                  {event.stage.kind === 'transit' ? tx("ui.samples.heading_to_95084afc") : ''}{place}
                  {event.shipment?.carrier?.name ? tx("ui.samples.name_554f1526", { name: event.shipment.carrier.name }) : ''}
                </small>
              </span>
            </button>
            <button type="button" className="xo-sample-list-open"
              aria-label={tx("ui.samples.open_activationcode_history_5d6ac455", { activationCode: event.sample.printedCode ?? event.sample.code })}
              onClick={() => onOpen(event.sample.uuid)}>
              <ChevronRight aria-hidden="true" />
            </button>
          </article>
        );
      })}
    </div>
  );
}

function SampleRail({
  samples, total, counts, stages, selected, query, stageCode, scope, location,
  view, activity, mayReadGlobal, onQuery, onStage, onScope, onView, onSelect, onOpen,
}) {
  return (
    <aside className="xo-sample-rail">
      <RailHeader title={tx("ui.samples.sample_tracking_6cd0bd6b")} />
      <WorkspaceTabs className="xo-sample-view-tabs is-rail" value={view}
        label={tx("ui.samples.sample_tracking_view_f1c3c10f")} onChange={onView}
        tabs={[
          { value: 'samples', label: tx("ui.samples.samples_0fb59422"), Icon: Package },
          { value: 'activity', label: tx("ui.samples.recent_activity_72d5617f"), Icon: History },
        ]} />
      <div className="xo-workspace-search">
        <SearchField value={query} onChange={onQuery}
          placeholder={view === 'activity' ? tx("ui.samples.search_recent_activity_56491418") : tx("ui.samples.search_samples_bcd203b3")}
          label={view === 'activity' ? tx("ui.samples.search_recent_activity_5cc16dc4") : tx("ui.samples.search_samples_351c9868")} />
      </div>
      <WorkspaceTabs className="xo-sample-tabs is-rail is-compact" value={scope}
        label={tx("ui.samples.sample_scope_c1ea746e")} onChange={onScope}
        tabs={[
          { value: 'local', label: tx("ui.samples.local_dc99d54d"), count: counts.local, Icon: MapPin },
          ...(mayReadGlobal ? [
            { value: 'global', label: tx("ui.samples.global_5f1184f7"), count: counts.global, Icon: Globe2 },
          ] : []),
        ]} />
      <div className="xo-sample-rail-filter">
        <XomicsSelect value={stageCode} aria-label={tx("ui.samples.filter_by_journey_step_85674eaf")} onChange={(event) => onStage(event.target.value)}>
          <option value="">{tx("ui.samples.all_journey_steps_1611ffef")}</option>
          {stages.map((stage) => <option key={stage.code} value={stage.code}>{codeLabel('sampleStage', stage.code)}</option>)}
        </XomicsSelect>
      </div>

      <div className="xo-sample-rail-list">
        {view === 'activity' ? (
          query.trim().length === 1 ? (
            <Empty title={tx("ui.samples.no_recent_activity_6a52f1d4")}
              detail={tx("ui.samples.try_another_search_scope_or_journey_step_654359dd")} />
          ) : (
            <RecentActivity activity={activity} selected={selected}
              onSelect={onSelect} onOpen={onOpen} />
          )
        ) : samples.length === 0 ? (
          <Empty title={tx("ui.samples.no_samples_found_14f0c310")} detail={tx("ui.samples.try_another_search_or_journey_step_69977397")} />
        ) : samples.map((sample) => {
          const completed = sample.completedSteps ?? [];
          const currentStep = completed[completed.length - 1] ?? sample.currentStage;
          const previousStep = completed.length > 1 ? completed[completed.length - 2] : null;
          const imageUrl = sample.product?.mainImageThumbnailUrl ?? null;
          return (
            <article className={`xo-sample-list-row${selected === sample.uuid ? ' is-selected' : ''}`}
              key={sample.uuid}>
              <div className="xo-sample-list-summary">
                <button type="button" className="xo-sample-list-select" onClick={() => onSelect(sample.uuid)}>
                  <span className="xo-sample-list-image">
                    {imageUrl === null
                      ? <Package aria-hidden="true" />
                      : <img src={imageUrl} alt="" width="52" height="52" loading="lazy" decoding="async" />}
                  </span>
                  <span className="xo-sample-list-copy">
                    <span><strong>{sample.printedCode ?? sample.code}</strong></span>
                    <span>{sample.product === null ? sample.template.name : sample.product.name}</span>
                    <small>{sample.at?.code ?? sample.originLocation?.code ?? sample.clinic?.name} ·{when(sample.lastEventAt)}{sample.organization ? ` · ${sample.organization.name}` : ''}</small>
                  </span>
                </button>
                <button type="button" className="xo-sample-list-open"
                  aria-label={tx("ui.samples.open_activationcode_details_d6d68493", { activationCode: sample.printedCode ?? sample.code })} onClick={() => onOpen(sample.uuid)}>
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>

              <div className="xo-sample-mini-journey" aria-label={tx("ui.samples.journey_preview_for_activationcode_7eef473c", { activationCode: sample.printedCode ?? sample.code })}>
                {previousStep !== null && (
                  <span className="is-previous" key={`previous-${previousStep.id}`}>
                    <i><Check aria-hidden="true" /></i>
                    <span><strong>{codeLabel('sampleStage', previousStep.code)}</strong><small>{tx("ui.samples.previous_50f94286")}</small></span>
                  </span>
                )}
                {currentStep !== null && (
                  <span className="is-current" key={`current-${currentStep.id ?? currentStep.code}`}>
                    <i aria-hidden="true" />
                    <span><strong>{codeLabel('sampleStage', currentStep.code)}</strong><small>{tx("ui.samples.current_4fc0e2bc")}</small></span>
                  </span>
                )}
                {sample.next !== null ? (
                  <span className="is-next">
                    <i><ChevronRight aria-hidden="true" /></i>
                    <span>
                      <strong>{codeLabel('sampleStageAction', sample.next.code)}</strong>
                      <small>{tx("ui.samples.proposed_next_0066f8dc")}</small>
                    </span>
                  </span>
                ) : (
                  <span className="is-next is-finished">
                    <i><CircleCheck aria-hidden="true" /></i>
                    <span><strong>{tx("ui.samples.journey_complete_25c2b888")}</strong><small>{tx("ui.samples.no_further_step_recommended_8c0d7a2c")}</small></span>
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <footer>
        <span>{view === 'activity'
          ? tx("ui.samples.value_recent_events_27b87b74", { value: activity.data?.events?.length ?? 0 })
          : tx("ui.samples.length_shown_403e9a9e", { length: samples.length })}</span>
        <span>{scope === 'local' ? location?.name ?? location?.code ?? tx("ui.samples.your_location_37e3a3f3") : tx("ui.samples.all_locations_797a4917")} · {total.toLocaleString()}</span>
      </footer>
    </aside>
  );
}

export default function XomicsSamples() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, user } = useXomicsAuth();
  const mayWrite = permissions.includes('SAMPLE_TRACK_WRITE');
  const mayShip = permissions.includes('SHIPMENT_MANAGE');
  const mayReadShip = permissions.includes('SHIPMENT_READ');
  const mayReadAllShipments = permissions.includes('SHIPMENT_READ_ALL');
  const mayUpload = permissions.includes('UPLOAD_CREATE');
  const mayReadGlobal = permissions.includes('SAMPLE_TRACK_READ_ALL');

  const [railView, setRailView] = useState('samples');
  const [scope, setScope] = useState('local');
  const [stageCode, setStageCode] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [opened, setOpened] = useState(null);
  const [pinnedUuid, setPinnedUuid] = useState(null);
  const [notice, setNotice] = useState('');
  const [journeyRevision, setJourneyRevision] = useState(0);
  const linkedSampleUuid = useMemo(() => {
    const value = String(searchParams.get('sampleUuid') ?? '').trim();
    return UUID_RE.test(value) ? value : null;
  }, [searchParams]);

  useEffect(() => {
    if (!mayReadGlobal && scope !== 'local') setScope('local');
  }, [mayReadGlobal, scope]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadJourney = useCallback(() => loadSampleStages(), []);
  const stages = useLiveResource(['samples'], loadJourney);
  const load = useCallback(
    async () => {
      const result = await loadSamples({ limit: 100, stageCode, scope });
      return { ...result, stageCode };
    },
    [scope, stageCode],
  );
  const list = useLiveResource(['samples'], load);
  const sampleSearchActive = railView === 'samples' && debouncedQuery.length >= 2;
  const loadSearch = useCallback(
    async () => {
      const result = await searchSamples({ query: debouncedQuery, limit: 50, stageCode, scope });
      return { ...result, stageCode };
    },
    [debouncedQuery, scope, stageCode],
  );
  const search = useLiveResource(['samples'], loadSearch, { enabled: sampleSearchActive });
  const loadPinned = useCallback(() => loadSample(pinnedUuid), [pinnedUuid]);
  const pinned = useLiveResource(['samples'], loadPinned, { enabled: pinnedUuid !== null });
  const loadActivity = useCallback(
    () => loadSampleActivity({
      limit: 100, stageCode, scope,
      query: debouncedQuery.length >= 2 ? debouncedQuery : '',
    }),
    [debouncedQuery, scope, stageCode],
  );
  const activity = useLiveResource(['samples'], loadActivity, { enabled: railView === 'activity' });
  const loadPlaces = useCallback(() => loadLocations(), []);
  const locationList = useLiveResource(['locations'], loadPlaces);

  const stageList = stages.data ?? [];
  const locations = locationList.data ?? [];
  const searchReady = search.data?.scope === scope && search.data?.query === debouncedQuery
    && search.data?.stageCode === stageCode;
  const listMatchesFilters = list.data?.scope === scope && list.data?.stageCode === stageCode;
  const pinnedReady = pinnedUuid !== null && pinned.data?.sample?.uuid === pinnedUuid;
  const listReady = pinnedReady || (sampleSearchActive ? searchReady : listMatchesFilters);
  const samples = useMemo(() => {
    const listed = sampleSearchActive
      ? (searchReady ? search.data.samples : [])
      : (listMatchesFilters ? list.data.samples : []);
    const direct = pinned.data?.sample?.uuid === pinnedUuid ? pinned.data.sample : null;
    if (direct === null || listed.some((sample) => sample.uuid === direct.uuid)) return listed;
    return [direct, ...listed];
  }, [list.data, listMatchesFilters, pinned.data, pinnedUuid, sampleSearchActive, search.data, searchReady]);
  const shown = useMemo(
    () => (query.trim().length === 1 ? [] : samples),
    [query, samples],
  );

  useEffect(() => {
    if (linkedSampleUuid === null) return;
    setRailView('samples');
    setScope(mayReadGlobal ? 'global' : 'local');
    setStageCode('');
    setQuery('');
    setPinnedUuid(linkedSampleUuid);
    setSelected(linkedSampleUuid);
    setOpened(linkedSampleUuid);
  }, [linkedSampleUuid, mayReadGlobal]);

  useEffect(() => {
    if (railView !== 'samples') return;
    if (pinnedUuid !== null) {
      if (selected !== pinnedUuid) setSelected(pinnedUuid);
      return;
    }
    if (shown.length === 0) {
      setSelected(null);
      return;
    }
    if (!shown.some((sample) => sample.uuid === selected)) setSelected(shown[0].uuid);
  }, [pinnedUuid, railView, shown, selected]);

  function selectSample(uuid) {
    if (uuid !== pinnedUuid) clearPinnedSample();
    setSelected(uuid);
  }

  function clearPinnedSample() {
    setPinnedUuid(null);
    setSelected(null);
    if (linkedSampleUuid !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete('sampleUuid');
      setSearchParams(next, { replace: true });
    }
  }

  function changeQuery(value) {
    clearPinnedSample();
    setQuery(value);
  }

  function changeStage(value) {
    clearPinnedSample();
    setStageCode(value);
  }

  function changeScope(value) {
    clearPinnedSample();
    setScope(value);
  }

  function changeView(value) {
    clearPinnedSample();
    setRailView(value);
  }

  function openSample(uuid) {
    selectSample(uuid);
    setOpened(uuid);
  }

  function closeOpenedSample() {
    setOpened(null);
    if (linkedSampleUuid === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete('sampleUuid');
    setSearchParams(next, { replace: true });
  }

  function changed(message) {
    setNotice(message);
    setJourneyRevision((value) => value + 1);
    list.refresh();
    activity.refresh();
  }

  function openUpload(sample) {
    if (!mayUpload || sample.patientId === null || sample.pipelines.length === 0
        || !sample.pipelines.some(p => p.available)) return;
    const params = new URLSearchParams({
      sampleUuid: sample.uuid,
      collectionId: String(sample.collectionId),
      patientId: String(sample.patientId),
    });
    setOpened(null);
    navigate(`/xomics/uploads?${params.toString()}`);
  }

  function openShipment(uuid) {
    setOpened(null);
    const params = new URLSearchParams({ shipmentUuid: uuid });
    if (mayReadAllShipments) params.set('scope', 'global');
    navigate(`/xomics/shipments?${params.toString()}`);
  }

  return (
    <>
      <div className="xo-sample-board">
        <main className="xo-samples-main">
          <Head title={tx("ui.samples.sample_tracking_6cd0bd6b")} subtitle={tx("ui.samples.follow_every_custody_checkpoint_from_activation_to_res_bc3ed532")} />
          {notice && (
            <div className="xo-alert t-ok xo-sample-notice" role="status">
              <CircleCheck aria-hidden="true" />
              <span>
                <strong>{tx("ui.samples.sample_journey_updated_c4b606c5")}</strong>
                <small>{tx("ui.samples.notice_the_map_movement_history_and_next_step_are_now__60bd7f20", { notice: notice })}</small>
              </span>
              <button type="button" aria-label={tx("ui.samples.dismiss_update_73aedc7c")} onClick={() => setNotice('')}>
                <X aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="xo-sample-map-shell">
            {selected === null ? (
              <Empty title={tx("ui.samples.choose_a_sample_baf778dd")} detail={tx("ui.samples.select_a_sample_on_the_right_to_plot_its_recorded_move_93f56c50")} />
            ) : (
              <JourneyMap uuid={selected} locations={locations} revision={journeyRevision} />
            )}
          </div>
        </main>

        {!listReady && !(sampleSearchActive ? search.error : list.error) ? (
          <aside className="xo-sample-rail"><Loading label={tx("ui.samples.finding_samples_868baf0f")} /></aside>
        ) : (sampleSearchActive ? search.error : list.error) ? (
          <aside className="xo-sample-rail"><Failed
            message={sampleSearchActive ? search.error : list.error}
            onRetry={sampleSearchActive ? search.refresh : list.refresh} /></aside>
        ) : (
          <SampleRail
            samples={shown}
            total={sampleSearchActive ? search.data.samples.length : list.data?.total ?? 0}
            counts={list.data?.counts ?? { global: 0, local: 0 }}
            stages={stageList}
            selected={selected}
            query={query}
            stageCode={stageCode}
            scope={scope}
            view={railView}
            activity={activity}
            mayReadGlobal={mayReadGlobal}
            location={user?.location ?? null}
            onQuery={changeQuery}
            onStage={changeStage}
            onScope={changeScope}
            onView={changeView}
            onSelect={selectSample}
            onOpen={openSample}
          />
        )}
      </div>

      {opened !== null && (
        <SampleDetailModal
          uuid={opened}
          stages={stageList}
          locations={locations}
          mayWrite={mayWrite}
          mayShip={mayShip}
          mayReadShip={mayReadShip}
          mayUpload={mayUpload}
          onClose={closeOpenedSample}
          onChanged={changed}
          onOpenShipment={openShipment}
          onOpenUpload={openUpload}
        />
      )}
    </>
  );
}
